import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ShoppingCart, Heart, User, LogOut, Music } from 'lucide-react';
import AuthModal from './AuthModal';

const Navbar = ({ cartCount = 0 }) => {
    const { user, logout, isAuthenticated } = useAuth();
    const [showAuthModal, setShowAuthModal] = useState(false);
    const [showUserMenu, setShowUserMenu] = useState(false);
    const navigate = useNavigate();

    const handleLogout = () => {
        logout();
        navigate('/');
    };

    return (
        <>
            <nav className="fixed top-0 w-full z-50 border-b border-white/5 glass-effect" data-testid="navbar">
                <div className="px-6 md:px-12 lg:px-24 py-4">
                    <div className="flex items-center justify-between">
                        <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
                            <Music className="w-8 h-8 text-primary" strokeWidth={1.5} />
                            <span className="text-xl font-bold tracking-tighter">BRIGHTFIELD AUDIO</span>
                        </Link>

                        <div className="hidden md:flex items-center gap-8">
                            <Link 
                                to="/browse" 
                                className="text-sm font-medium text-muted-foreground hover:text-white transition-colors uppercase tracking-widest"
                                data-testid="browse-link"
                            >
                                Browse
                            </Link>
                            <Link 
                                to="/sample-packs" 
                                className="text-sm font-medium text-muted-foreground hover:text-white transition-colors uppercase tracking-widest"
                                data-testid="sample-packs-link"
                            >
                                Sample Packs
                            </Link>
                            <Link 
                                to="/songs" 
                                className="text-sm font-medium text-muted-foreground hover:text-white transition-colors uppercase tracking-widest"
                                data-testid="songs-link"
                            >
                                Songs
                            </Link>
                            <Link 
                                to="/albums" 
                                className="text-sm font-medium text-muted-foreground hover:text-white transition-colors uppercase tracking-widest"
                                data-testid="albums-link"
                            >
                                Albums
                            </Link>
                        </div>

                        <div className="flex items-center gap-4">
                            {isAuthenticated ? (
                                <>
                                    <Link to="/wishlist" className="p-3 rounded-full hover:bg-white/10 transition-colors" data-testid="wishlist-button">
                                        <Heart className="w-5 h-5" strokeWidth={1.5} />
                                    </Link>
                                    <Link to="/cart" className="p-3 rounded-full hover:bg-white/10 transition-colors relative" data-testid="cart-button">
                                        <ShoppingCart className="w-5 h-5" strokeWidth={1.5} />
                                        {cartCount > 0 && (
                                            <span className="absolute -top-1 -right-1 bg-primary text-white text-xs w-5 h-5 flex items-center justify-center rounded-full" data-testid="cart-count">
                                                {cartCount}
                                            </span>
                                        )}
                                    </Link>
                                    <div className="relative">
                                        <button 
                                            onClick={() => setShowUserMenu(!showUserMenu)}
                                            className="p-3 rounded-full hover:bg-white/10 transition-colors"
                                            data-testid="user-menu-button"
                                        >
                                            <User className="w-5 h-5" strokeWidth={1.5} />
                                        </button>
                                        {showUserMenu && (
                                            <div className="absolute right-0 mt-2 w-48 bg-black border border-white/10 rounded-sm overflow-hidden" data-testid="user-dropdown">
                                                <Link 
                                                    to="/dashboard" 
                                                    className="block px-4 py-3 hover:bg-white/5 text-sm"
                                                    onClick={() => setShowUserMenu(false)}
                                                    data-testid="dashboard-link"
                                                >
                                                    Dashboard
                                                </Link>
                                                {user?.is_admin && (
                                                    <Link 
                                                        to="/admin" 
                                                        className="block px-4 py-3 hover:bg-white/5 text-sm border-t border-white/10"
                                                        onClick={() => setShowUserMenu(false)}
                                                        data-testid="admin-link"
                                                    >
                                                        Admin Panel
                                                    </Link>
                                                )}
                                                <button 
                                                    onClick={handleLogout}
                                                    className="w-full text-left px-4 py-3 hover:bg-white/5 text-sm flex items-center gap-2"
                                                    data-testid="logout-button"
                                                >
                                                    <LogOut className="w-4 h-4" strokeWidth={1.5} />
                                                    Logout
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </>
                            ) : (
                                <button 
                                    onClick={() => setShowAuthModal(true)}
                                    className="btn-primary px-6 py-3"
                                    data-testid="login-button"
                                >
                                    Login
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </nav>

            {showAuthModal && <AuthModal onClose={() => setShowAuthModal(false)} />}
        </>
    );
};

export default Navbar;
